export class SalesEvent {
    /**
     * Run the campaign logic, do cart modifications and trigger the widget update
     * @param config Sales event config set up in the theme
     * @param cart Current cart data
     */
    static run(config, cart) {
        if (!config || !cart)
            return;
        const configValidity = SalesEvent.checkConfigValidity(config);
        if (!configValidity.valid) {
            document.querySelector('[data-se-element="salesEventWidget"]').innerHTML = `Config error: ${configValidity.message}`;
            return;
        }
        SalesEvent.config = config;
        SalesEvent.cart = cart;
        SalesEvent.uiData = {
            blocked: false,
            headline: config.headline,
            thresholds: [],
            cartValue: SalesEvent.cart.items_subtotal_price
        };
        const shouldRunCampaign = SalesEvent.shouldRunCampaign();
        if (SalesEvent.config.tieredGifts !== null)
            SalesEvent.runTieredGifts(shouldRunCampaign);
        if (SalesEvent.config.tieredDiscounts !== null)
            SalesEvent.runTieredDiscounts();
        if (SalesEvent.config.giftsWithProduct !== null)
            SalesEvent.runGiftsWithProduct(shouldRunCampaign);
        SalesEvent.uiData.thresholds.sort((a, b) => a.cartValue - b.cartValue);
        SalesEvent.uiData.blocked = !shouldRunCampaign;
        SalesEvent.display();
    }
    static runGiftsWithProduct(shouldRunCampaign) {
        const config = SalesEvent.config.giftsWithProduct;
        const cartOperations = [];
        // Cart items that qualify as "purchased product" (not gifts themselves)
        const eligibleCartItems = SalesEvent.cart.items.filter(item => !item.properties.some(p => p[0] === '_sales-event-gift' && p[1] === 'true')
            && config.purchasedProducts.some(pp => pp.id === item.product.id && pp.variants.some(v => v.id === item.variant.id)));
        // Free gift lines already in the cart
        const freeGiftsInCart = SalesEvent.cart.items.filter(item => item.properties.some(p => p[0] === '_sales-event-gift' && p[1] === 'true')
            && config.freeProducts.some(fp => fp.id === item.product.id));
        const purchasedProductInCart = eligibleCartItems.length > 0;
        const freeGiftInCart = freeGiftsInCart.length > 0;
        const currentGiftCount = freeGiftsInCart.reduce((sum, g) => sum + g.quantity, 0);
        const targetGiftCount = shouldRunCampaign && purchasedProductInCart
            ? (config.multipleGifts ? eligibleCartItems.reduce((sum, item) => sum + item.quantity, 0) : 1)
            : 0;
        if (currentGiftCount > targetGiftCount) {
            let toRemove = currentGiftCount - targetGiftCount;
            for (const giftLine of freeGiftsInCart) {
                if (toRemove <= 0)
                    break;
                const removeQty = Math.min(giftLine.quantity, toRemove);
                cartOperations.push({ action: 'update', id: giftLine.key, quantity: giftLine.quantity - removeQty });
                toRemove -= removeQty;
            }
        }
        else if (currentGiftCount < targetGiftCount) {
            if (config.autoAdd) {
                const availableProduct = SalesEvent.findFirstAvailableProduct(config.freeProducts);
                if (availableProduct) {
                    const availableVariant = SalesEvent.findFirstAvailableVariant(availableProduct.variants);
                    if (availableVariant) {
                        cartOperations.push({
                            action: 'add',
                            id: availableVariant.id,
                            quantity: targetGiftCount - currentGiftCount,
                            properties: { '_sales-event-gift': 'true' }
                        });
                    }
                }
            }
            else {
                SalesEvent.uiData.thresholds.push({
                    cartValue: 0,
                    rewards: [{
                            type: 'gift',
                            products: config.freeProducts,
                            productDisplayTitle: config.freeProductDisplayTitle,
                            productDisplayImage: config.freeProductDisplayImage,
                            applied: purchasedProductInCart && freeGiftInCart && currentGiftCount >= targetGiftCount,
                            consecutiveGifts: 1
                        }]
                });
            }
        }
        if (cartOperations.length > 0)
            SalesEvent.updateCart(cartOperations);
    }
    /**
     * Run the tiered gifts logic, build UI data and do cart modifications if needed
     * @param shouldRunCampaign True, if the sales event is not blocked to incompatible discounts
     */
    static runTieredGifts(shouldRunCampaign) {
        const giftsInCart = [];
        const giftUsageMap = new Map();
        const complementaryGiftsToRemove = [];
        const complementaryGiftsInCart = [];
        const cartOperations = [];
        for (const item of SalesEvent.cart.items) {
            if (item.properties.some(p => p[0] === '_sales-event-gift' && p[1] === 'true')) {
                giftsInCart.push(item);
                giftUsageMap.set(item, 0);
            }
            else if (item.product.isGift) {
                complementaryGiftsInCart.push(item);
            }
        }
        let cartTotalWithoutGifts = SalesEvent.cart.items_subtotal_price - giftsInCart.reduce((sum, item) => sum + item.finalLinePrice, 0);
        complementaryGiftsInCart.forEach(item => {
            // Exclude complementary gifts from cart total when they are associated with a threshold product to prevent infinity cart change loops
            if (Object.values(SalesEvent.config.tieredGifts.thresholds).some(threshold => threshold.enabled && threshold.products.some(product => product.gifts.some(gift => gift.id === item.product.id))))
                cartTotalWithoutGifts -= item.finalLinePrice;
        });
        SalesEvent.uiData.cartValue = cartTotalWithoutGifts;
        const validGifts = [];
        for (let i = 3; i >= 1; i--) {
            const thresholdData = SalesEvent.config.tieredGifts.thresholds[i];
            if (!thresholdData.enabled)
                continue;
            // Push threshold data to UI data
            SalesEvent.uiData.thresholds.push({
                cartValue: thresholdData.cartValue,
                rewards: [{
                        type: 'gift',
                        products: thresholdData.products,
                        productDisplayTitle: thresholdData.productDisplayTitle,
                        productDisplayImage: thresholdData.productDisplayImage,
                        applied: false, // Will be updated later
                        consecutiveGifts: 1 // Will be updated later based on other thresholds
                    }]
            });
            // Check for consecutive gifts for same product across thresholds
            if (!SalesEvent.config.tieredGifts.singleGift) {
                SalesEvent.uiData.thresholds.forEach(threshold => {
                    if (threshold.cartValue === thresholdData.cartValue)
                        return;
                    threshold.rewards.forEach(reward => {
                        if (reward.type === 'gift' && reward.productDisplayTitle === thresholdData.productDisplayTitle)
                            reward.consecutiveGifts++;
                    });
                });
            }
            if (cartTotalWithoutGifts < thresholdData.cartValue)
                continue;
            if (thresholdData.cartValue === 0 && SalesEvent.cart?.items.length === 0)
                continue; // Prevent granting free gifts on empty carts
            if (SalesEvent.config.tieredGifts.singleGift && validGifts.length > 0)
                continue;
            validGifts.push({
                products: thresholdData.products,
                used: false
            });
        }
        // Check which gifts are applied in the correct order
        for (let i = SalesEvent.uiData.thresholds.length - 1; i >= 0; i--) {
            const thresholdData = SalesEvent.uiData.thresholds[i];
            thresholdData.rewards.forEach(reward => {
                reward.applied = thresholdData.cartValue <= SalesEvent.uiData.cartValue && giftsInCart.some(g => {
                    if (!(reward.products.some(p => p.id === g.product.id && p.variants.some(v => v.id === g.variant.id))))
                        return false;
                    if (giftUsageMap.get(g) >= g.quantity)
                        return false;
                    giftUsageMap.set(g, giftUsageMap.get(g) + 1);
                    return true;
                });
            });
        }
        for (const giftLine of giftsInCart) {
            const giftProductId = giftLine.product.id;
            let validQuantity = 0;
            if (shouldRunCampaign) {
                for (const validGift of validGifts) {
                    if (validGift.used)
                        continue;
                    if (validGift.products.some(p => p.id === giftProductId && p.variants.some((v) => v.id === giftLine.variant.id))) {
                        validGift.used = true;
                        validQuantity++;
                        if (validQuantity >= giftLine.quantity)
                            break;
                    }
                }
            }
            if (validQuantity < giftLine.quantity)
                cartOperations.push({ action: 'update', id: giftLine.key, quantity: validQuantity });
            if (validQuantity === 0) {
                for (const complementaryGift of complementaryGiftsInCart) {
                    if (complementaryGift.properties.some(p => p[0] === '_sales-event-complementary-gift' && p[1] === giftProductId.toString())) {
                        cartOperations.push({ action: 'update', id: complementaryGift.key, quantity: 0 });
                        complementaryGiftsToRemove.push({ productId: complementaryGift.product.id });
                    }
                }
            }
        }
        if (shouldRunCampaign && SalesEvent.config.tieredGifts.autoAdd) {
            const missingGifts = [];
            for (const validGift of validGifts) {
                if (validGift.used)
                    continue;
                const existingMissingGift = missingGifts.find(g => validGift.products.some(p => p.id === g.product.id && SalesEvent.arrayEquals(p.variants.map((v) => v.id), g.product.variants.map((v) => v.id))));
                if (existingMissingGift) {
                    existingMissingGift.quantity += 1;
                }
                else {
                    const missingProduct = SalesEvent.findFirstAvailableProduct(validGift.products);
                    if (!missingProduct)
                        continue;
                    missingGifts.push({ product: missingProduct, quantity: 1 });
                    if (SalesEvent.config.tieredGifts.includeComplementaryGifts) {
                        for (const complementaryGift of missingProduct.gifts) {
                            if (missingGifts.some(g => g.product.id === complementaryGift.id) || SalesEvent.cart?.items.some(i => i.product.id === complementaryGift.id) && !complementaryGiftsToRemove.some(r => r.productId === complementaryGift.id))
                                continue;
                            missingGifts.push({ product: complementaryGift, quantity: 1, grantingProductId: missingProduct.id });
                        }
                    }
                }
            }
            for (const missingGift of missingGifts) {
                const availableGiftVariant = SalesEvent.findFirstAvailableVariant(missingGift.product.variants);
                if (!availableGiftVariant)
                    continue;
                const operation = {
                    action: 'add',
                    id: availableGiftVariant.id,
                    quantity: missingGift.quantity,
                    properties: {}
                };
                if (missingGift.grantingProductId) {
                    operation.properties['_sales-event-complementary-gift'] = missingGift.grantingProductId.toString();
                }
                else {
                    operation.properties['_sales-event-gift'] = 'true';
                }
                cartOperations.push(operation);
            }
        }
        if (cartOperations.length > 0)
            SalesEvent.updateCart(cartOperations);
    }
    /**
     * Run the tiered discounts logic by building UI data
     */
    static runTieredDiscounts() {
        for (let i = 3; i >= 1; i--) {
            const thresholdData = SalesEvent.config.tieredDiscounts.thresholds[i];
            if (!thresholdData.enabled)
                continue;
            const existingUiDataThreshold = SalesEvent.uiData.thresholds.find(t => t.cartValue === thresholdData.cartValue);
            if (existingUiDataThreshold) {
                existingUiDataThreshold.rewards.push({
                    type: 'discount',
                    value: thresholdData.amount,
                    valueType: thresholdData.type,
                    applied: thresholdData.cartValue <= SalesEvent.uiData.cartValue
                });
            }
            else {
                SalesEvent.uiData.thresholds.push({
                    cartValue: thresholdData.cartValue,
                    rewards: [{
                            type: 'discount',
                            value: thresholdData.amount,
                            valueType: thresholdData.type,
                            applied: thresholdData.cartValue <= SalesEvent.uiData.cartValue
                        }]
                });
            }
        }
    }
    /**
     * Checks if a sales event config is valid using several rules
     * @param config Sales event config
     * @returns Object with validity and a message on error
     */
    static checkConfigValidity(c) {
        if (!c)
            return { valid: false, message: 'No config provided' };
        if (c.tieredGifts === null && c.tieredDiscounts === null && c.giftsWithProduct === null)
            return { valid: false, message: 'No campaign type enabled' };
        if (c.giftsWithProduct !== null && (c.tieredGifts !== null || c.tieredDiscounts !== null))
            return { valid: false, message: 'Gifts with products cannot be combined with tiered gifts or discounts' };
        if (c.tieredGifts !== null) {
            if (Object.values(c.tieredGifts.thresholds).every(t => !t.enabled))
                return { valid: false, message: 'No tiered gifts threshold enabled' };
            const productSettings = [];
            let productsAreIdenticalOrDisjoint = true;
            Object.values(c.tieredGifts.thresholds).forEach(t => {
                if (!t.enabled || !productsAreIdenticalOrDisjoint)
                    return;
                // Check for same products across thresholds with different variants
                if (productSettings.some(setting => {
                    for (const p of t.products) {
                        for (const sp of setting.products) {
                            if (p.id !== sp.id)
                                continue;
                            if (!SalesEvent.arrayEquals(p.variants, sp.variants) && !SalesEvent.arraysAreDisjoint(p.variants, sp.variants))
                                return true;
                        }
                    }
                    return false;
                }))
                    productsAreIdenticalOrDisjoint = false;
                productSettings.push({ products: t.products });
            });
            if (!productsAreIdenticalOrDisjoint)
                return { valid: false, message: 'Tiered gifts product variants of thresholds need to be either identical or disjoint' };
        }
        if (c.tieredDiscounts !== null) {
            if (Object.values(c.tieredDiscounts.thresholds).every(t => !t.enabled))
                return { valid: false, message: 'No tiered discounts threshold enabled' };
        }
        if (c.giftsWithProduct !== null) {
            if (c.giftsWithProduct.purchasedProducts.length === 0)
                return { valid: false, message: 'No purchased products configured for gifts with products' };
            if (c.giftsWithProduct.freeProducts.length === 0)
                return { valid: false, message: 'No free products configured for gifts with products' };
        }
        return { valid: true };
    }
    /**
     * Update the cart with given operations
     * @param operations Cart change operations
     */
    static updateCart(operations) {
        window.Cart.changeCartItems(operations);
    }
    /**
     * Determine whether the sales event campaign should run based on cart attributes
     * @returns True or false
     */
    static shouldRunCampaign() {
        if (SalesEvent.cart?.attributes['_from'] && ['sales-page', 'crm'].includes(SalesEvent.cart?.attributes['_from']))
            return false;
        return true;
    }
    /**
     * Search for the first available variant in the given variants array
     * @param variants Product variants
     * @returns Available variant or null
     */
    static findFirstAvailableVariant(variants) {
        for (const variant of variants) {
            if (variant.available)
                return variant;
        }
        return null;
    }
    /**
     * Search for the first product with an available variant in the given products array
     * @param products Products
     * @returns Available product or null
     */
    static findFirstAvailableProduct(products) {
        for (const product of products) {
            let availableVariant = SalesEvent.findFirstAvailableVariant(product.variants);
            if (availableVariant)
                return product;
        }
        return null;
    }
    /**
     * Checks whether two arrays do contain the same items in the same order
     * @param a Array A
     * @param b Array B
     * @returns True, if arrays are equal
     */
    static arrayEquals(a, b) {
        if (a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; i++) {
            if (a[i] instanceof Object && b[i] instanceof Object) {
                if (JSON.stringify(a[i]) !== JSON.stringify(b[i]))
                    return false;
                continue;
            }
            if (a[i] !== b[i])
                return false;
        }
        return true;
    }
    /**
     * Checks whether two arrays are disjoint (no common items)
     * @param a Array A
     * @param b Array B
     * @returns True, if arrays are disjoint
     */
    static arraysAreDisjoint(a, b) {
        for (const itemA of a) {
            for (const itemB of b) {
                if (itemA === itemB)
                    return false;
            }
        }
        return true;
    }
    /**
     * Fill the sales event widget with calculated data
     */
    static display() {
        SalesEvent.widget = document.querySelector('[data-se-element="salesEventWidget"]');
        if (!SalesEvent.widget)
            return;
        if (SalesEvent.uiData.thresholds.length === 0) {
            SalesEvent.widget.classList.add('none');
            return;
        }
        SalesEvent.getElement('headline').innerHTML = SalesEvent.uiData.headline;
        SalesEvent.displayProgress();
        // Build progress bar thresholds and determine current threshold
        const currentThreshold = SalesEvent.determineThresholdToDisplay();
        if (SalesEvent.uiData.blocked) {
            // If sales events are blocked due to incompatible discount codes, show blocked message
            SalesEvent.getElement('text').classList.add('none');
            SalesEvent.getElement('blockedHint').classList.remove('none');
        }
        else if (currentThreshold) {
            SalesEvent.displayCurrentThreshold(currentThreshold);
        }
        else {
            // All thresholds reached
            let translation;
            if (SalesEvent.uiData.thresholds.some(t => t.rewards.some(r => r.type === 'gift'))) {
                translation = SalesEvent.uiData.thresholds.length > 1 ? SalesEvent.getTranslation('giftsClaimedHtml') : SalesEvent.getTranslation('giftClaimedHtml');
            }
            else {
                translation = SalesEvent.uiData.thresholds.length > 1 ? SalesEvent.getTranslation('discountsClaimedHtml') : SalesEvent.getTranslation('discountClaimedHtml');
            }
            SalesEvent.getElement('text').innerHTML = translation;
        }
    }
    /**
     * Update the widget progress bar and thresholds
     */
    static displayProgress() {
        // No progress bar needed for single 0 value threshold
        if (SalesEvent.uiData.thresholds.length === 1 && SalesEvent.uiData.thresholds[0].cartValue === 0)
            return;
        SalesEvent.getElement('progressIndicator').classList.remove('none');
        const maxThresholdCartValue = SalesEvent.uiData.thresholds.reduce((acc, t) => t.cartValue > acc ? t.cartValue : acc, 0);
        const barTemplate = SalesEvent.getTemplate('bar');
        const unreachedIconTemplate = SalesEvent.getTemplate('iconUnreached');
        const reachedIconTemplate = SalesEvent.getTemplate('iconReached');
        for (const [index, threshold] of SalesEvent.uiData.thresholds.entries()) {
            const lastThresholdCartValue = index > 0 && index <= SalesEvent.uiData.thresholds.length - 1 ? SalesEvent.uiData.thresholds[index - 1].cartValue : 0;
            const thresholdPercentage = Math.min((threshold.cartValue - lastThresholdCartValue) / maxThresholdCartValue, 1) * 100;
            const progressPercentage = threshold.cartValue > SalesEvent.uiData.cartValue ? Math.min(Math.max(SalesEvent.uiData.cartValue - lastThresholdCartValue, 0) / (threshold.cartValue - lastThresholdCartValue), 1) * 100 : 100;
            const bar = barTemplate.cloneNode(true);
            if (index > 0 || threshold.cartValue > 0 || SalesEvent.uiData.thresholds.length === 1)
                bar.classList.remove('none');
            bar.style.setProperty('--se-bar-width', `${thresholdPercentage}%`);
            bar.style.setProperty('--se-bar-progress-width', `${progressPercentage}%`);
            bar.style.setProperty('--se-bar-reached', threshold.cartValue <= SalesEvent.uiData.cartValue ? '1' : '0');
            if (index > 0) {
                bar.style.setProperty('--se-border-radius-l', '0');
            }
            if (index < SalesEvent.uiData.thresholds.length - 1) {
                bar.style.setProperty('--se-border-radius-r', '0');
            }
            barTemplate.insertAdjacentElement('beforebegin', bar);
            const icon = threshold.cartValue <= SalesEvent.uiData.cartValue ? reachedIconTemplate.cloneNode(true) : unreachedIconTemplate.cloneNode(true);
            icon.classList.remove('none');
            bar.insertAdjacentElement('afterend', icon);
        }
    }
    /**
     * Determine which threshold to display in the widget, can be either the next threshold to reach or a reached threshold with unclaimed gifts
     * @returns Threshold to display or null if no threshold should be displayed
     */
    static determineThresholdToDisplay() {
        let currentThreshold = null;
        for (let i = 0; i < SalesEvent.uiData.thresholds.length; i++) {
            const threshold = SalesEvent.uiData.thresholds[i];
            // Determine current threshold based on mode and gifts applied
            if (SalesEvent.config.tieredGifts !== null && SalesEvent.config.tieredGifts.singleGift) {
                // For single gift mode, only one gift can be claimed, so we need to find the next unclaimed gift threshold
                if (SalesEvent.config.tieredGifts.autoAdd === false) {
                    if (threshold.cartValue <= SalesEvent.uiData.cartValue) {
                        if (threshold.rewards.some(r => r.type === 'gift' && !r.applied)) {
                            currentThreshold = threshold;
                        }
                        else if (threshold.rewards.some(r => r.type === 'gift') && threshold.rewards.every(r => r.applied)) {
                            currentThreshold = null;
                        }
                    }
                    else if (threshold.cartValue > SalesEvent.uiData.cartValue && !currentThreshold) {
                        currentThreshold = threshold;
                    }
                }
                else if (!currentThreshold && threshold.cartValue > SalesEvent.uiData.cartValue) {
                    currentThreshold = threshold;
                }
            }
            else if (SalesEvent.config.tieredGifts !== null && SalesEvent.config.tieredGifts.autoAdd === false) {
                // For manual adding mode, the current threshold can either be a reached threshold with unclaimed gifts, or the next threshold to reach
                if (!currentThreshold && threshold.cartValue <= SalesEvent.uiData.cartValue && threshold.rewards.some(r => r.type === 'gift' && !r.applied)) {
                    currentThreshold = threshold;
                }
                else if (!currentThreshold && threshold.cartValue > SalesEvent.uiData.cartValue) {
                    currentThreshold = threshold;
                }
            }
            else if (SalesEvent.config.giftsWithProduct !== null) {
                // For gifts with product, show the threshold whenever there's an unclaimed gift reward
                if (!currentThreshold && threshold.rewards.some(r => r.type === 'gift' && !r.applied)) {
                    currentThreshold = threshold;
                }
            }
            else {
                // For auto gifts and discounts, rewards of reached thresholds are always applied
                if (!currentThreshold && threshold.cartValue > SalesEvent.uiData.cartValue) {
                    currentThreshold = threshold;
                }
            }
        }
        return currentThreshold;
    }
    /**
     * Display the current threshold, meaning either the next threshold to reach or a form to add the next unclaimed gift is displayed
     * @param threshold Threshold to display
     */
    static displayCurrentThreshold(threshold) {
        const nextGift = threshold.rewards.find(r => r.type === 'gift' && !r.applied);
        const nextGiftFirstAvailableProduct = nextGift ? SalesEvent.findFirstAvailableProduct(nextGift.products) : null;
        const nextDiscount = threshold.rewards.find(r => r.type === 'discount');
        let productDisplayTitle = '';
        if (nextGift && nextGiftFirstAvailableProduct) {
            const productDisplayImage = nextGift.productDisplayImage || nextGiftFirstAvailableProduct.image;
            const imageHtml = SalesEvent.generateImageHtml({
                url: productDisplayImage.url,
                aspectRatio: 1,
                width: productDisplayImage.width,
                height: productDisplayImage.height,
                alt: nextGift.productDisplayTitle || nextGiftFirstAvailableProduct.title,
                sizes: '56px'
            });
            const imageElement = SalesEvent.getElement('currentThresholdImage');
            imageElement.innerHTML = imageHtml;
            imageElement.classList.remove('none');
            productDisplayTitle = nextGift.productDisplayTitle || nextGiftFirstAvailableProduct.title;
        }
        if (threshold.cartValue <= SalesEvent.uiData.cartValue) {
            // Threshold reached, show either add to cart button or variant selector for gift
            if (nextGift) {
                const firstAvailableProduct = SalesEvent.findFirstAvailableProduct(nextGift.products);
                SalesEvent.getElement('productTitle').innerHTML = SalesEvent.getTranslation('freeProduct', {
                    product: productDisplayTitle,
                    consecutive: nextGift.consecutiveGifts > 1 ? `${nextGift.consecutiveGifts}. ` : ''
                });
                const productAddButton = SalesEvent.getElement('productAddButton');
                if (nextGift.products.length > 1 || nextGift.products[0].variants.length > 1) {
                    const variantSelectorElement = SalesEvent.getElement('variantSelector');
                    const variantSelectorLabel = SalesEvent.getElement('variantSelectorLabel');
                    const variantOptionTemplate = SalesEvent.getElement('variantSelectorOption');
                    nextGift.products.forEach(p => {
                        p.variants.forEach(v => {
                            const optionElement = variantOptionTemplate.cloneNode(true);
                            optionElement.value = v.id.toString();
                            if (p.hasOnlyDefaultVariant) {
                                optionElement.textContent = p.title + (v.available ? '' : ` (${SalesEvent.getTranslation('soldOut')})`);
                            }
                            else {
                                optionElement.textContent = (nextGift.products.length > 1 ? p.title + ' - ' : '') + v.title + (v.available ? '' : ` (${SalesEvent.getTranslation('soldOut')})`);
                            }
                            optionElement.disabled = !v.available;
                            variantSelectorElement.appendChild(optionElement);
                        });
                    });
                    const onAddWithoutVariant = () => {
                        variantSelectorLabel.classList.add('error');
                    };
                    productAddButton.classList.remove('GlobalAddToCart');
                    productAddButton.addEventListener('click', onAddWithoutVariant);
                    variantSelectorElement.addEventListener('change', () => {
                        // Remove old complementary gift checkboxes
                        SalesEvent.widget?.querySelectorAll('[data-se-element="complementaryGiftCheckboxLabel"]').forEach(el => {
                            el.parentElement?.classList.add('none');
                            el.remove();
                        });
                        const value = variantSelectorElement.value;
                        if (!value) {
                            productAddButton.classList.remove('GlobalAddToCart');
                            productAddButton.addEventListener('click', onAddWithoutVariant);
                            return;
                        }
                        const associatedProduct = nextGift.products.find((p) => p.variants.some((v) => v.id === +value));
                        const productAddData = {
                            items: [{
                                    id: +value,
                                    productId: associatedProduct.id,
                                    quantity: 1,
                                    properties: {
                                        '_sales-event-gift': 'true'
                                    }
                                }]
                        };
                        productAddButton.classList.add('GlobalAddToCart');
                        productAddButton.removeEventListener('click', onAddWithoutVariant);
                        productAddButton.setAttribute('data-products', JSON.stringify(productAddData));
                        variantSelectorLabel.classList.remove('error');
                        if (SalesEvent.config.tieredGifts !== null && SalesEvent.config.tieredGifts.includeComplementaryGifts)
                            SalesEvent.insertComplementaryGiftCheckboxes(associatedProduct.gifts, associatedProduct);
                    });
                    SalesEvent.getElement('variantSelectorLabel').classList.remove('none');
                }
                else {
                    const firstAvailableVariant = SalesEvent.findFirstAvailableVariant(firstAvailableProduct.variants);
                    if (firstAvailableVariant) {
                        const productAddData = {
                            items: [{
                                    id: firstAvailableVariant.id,
                                    productId: firstAvailableProduct.id,
                                    quantity: 1,
                                    properties: {
                                        '_sales-event-gift': 'true'
                                    }
                                }]
                        };
                        productAddButton.setAttribute('data-products', JSON.stringify(productAddData));
                        if (SalesEvent.config.tieredGifts !== null && SalesEvent.config.tieredGifts.includeComplementaryGifts)
                            SalesEvent.insertComplementaryGiftCheckboxes(firstAvailableProduct.gifts, firstAvailableProduct);
                    }
                    else {
                        productAddButton.disabled = true;
                        productAddButton.innerHTML = SalesEvent.getTranslation('soldOut');
                    }
                }
                SalesEvent.getElement('currentThresholdAction').classList.remove('none');
                SalesEvent.getElement('progressIndicator').classList.add('none');
            }
            SalesEvent.getElement('currentThresholdText').classList.add('none');
        }
        else {
            // Threshold not yet reached, show which rewards are available at this threshold and the remaining amount
            let translation;
            if (nextGift) {
                if (nextDiscount) {
                    translation = SalesEvent.getTranslation('remainingAmountForMixedHtml', {
                        amount: window.Currency.formatMoney(threshold.cartValue - SalesEvent.uiData.cartValue, window.theme.moneyFormat),
                        products: productDisplayTitle,
                        discount: nextDiscount.valueType === 'percentage' ? `${nextDiscount.value.toString()}%` : window.Currency.formatMoney(nextDiscount.value, window.theme.moneyFormat),
                        consecutive: nextGift.consecutiveGifts > 1 ? `${nextGift.consecutiveGifts}. ` : ''
                    });
                }
                else {
                    translation = SalesEvent.getTranslation('remainingAmountForGiftHtml', {
                        amount: window.Currency.formatMoney(threshold.cartValue - SalesEvent.uiData.cartValue, window.theme.moneyFormat),
                        products: productDisplayTitle,
                        consecutive: nextGift.consecutiveGifts > 1 ? `${nextGift.consecutiveGifts}. ` : ''
                    });
                }
            }
            else if (nextDiscount) {
                translation = SalesEvent.getTranslation('remainingAmountForDiscountHtml', {
                    amount: window.Currency.formatMoney(threshold.cartValue - SalesEvent.uiData.cartValue, window.theme.moneyFormat),
                    discount: nextDiscount.valueType === 'percentage' ? `${nextDiscount.value.toString()}%` : window.Currency.formatMoney(nextDiscount.value, window.theme.moneyFormat)
                });
            }
            SalesEvent.getElement('currentThresholdText').innerHTML = translation || '';
        }
        SalesEvent.getElement('currentThreshold').classList.remove('none');
        SalesEvent.getElement('text').classList.add('none');
    }
    /**
     * Add complementary gift checkboxes to the sales event widget
     * @param complementaryGifts Complementary gift products
     * @param associatedProduct Product associated with the complementary gifts
     */
    static insertComplementaryGiftCheckboxes(complementaryGifts, associatedProduct) {
        const productAddButton = SalesEvent.getElement('productAddButton');
        for (const complementaryGift of complementaryGifts) {
            if (SalesEvent.cart?.items.some(i => i.product.id === complementaryGift.id))
                continue;
            const availableVariant = SalesEvent.findFirstAvailableVariant(complementaryGift.variants);
            if (!availableVariant)
                continue;
            const checkboxLabelElement = SalesEvent.getTemplate('complementaryGiftCheckboxLabel');
            const checkboxLabelElementClone = checkboxLabelElement.cloneNode(true);
            checkboxLabelElementClone.setAttribute('data-se-element', 'complementaryGiftCheckboxLabel');
            checkboxLabelElementClone.removeAttribute('data-se-template');
            SalesEvent.getElement('complementaryGiftCheckboxText', checkboxLabelElementClone).innerHTML = SalesEvent.getTranslation('includeComplementaryGift', {
                product: complementaryGift.title
            });
            const checkboxInputElement = SalesEvent.getElement('complementaryGiftCheckbox', checkboxLabelElementClone);
            checkboxInputElement.value = availableVariant.id.toString();
            checkboxInputElement.addEventListener('change', () => {
                const variantId = checkboxInputElement.value;
                const addData = JSON.parse(productAddButton.getAttribute('data-products') || '{"items":[]}');
                if (!variantId || !addData.items.length)
                    return;
                if (checkboxInputElement.checked) {
                    if (addData.items.some((item) => item.id === +variantId))
                        return;
                    addData.items.push({
                        id: +variantId,
                        productId: associatedProduct.id,
                        quantity: 1,
                        properties: {
                            '_sales-event-complementary-gift': associatedProduct.id.toString()
                        }
                    });
                }
                else {
                    const addItemIndex = addData.items.findIndex((item) => item.id === +variantId);
                    if (addItemIndex === -1)
                        return;
                    addData.items.splice(addItemIndex, 1);
                }
                productAddButton.setAttribute('data-products', JSON.stringify(addData));
            });
            checkboxLabelElementClone.classList.remove('none');
            checkboxLabelElement.insertAdjacentElement('afterend', checkboxLabelElementClone);
            checkboxLabelElement.parentElement?.classList.remove('none');
        }
    }
    /**
     * Helper to find an element inside the widget
     * @param name Widget element name
     * @param container Optional container within widget to look for the element
     * @returns Widget element or null if not found
     */
    static getElement(name, container) {
        return (container || SalesEvent.widget)?.querySelector(`[data-se-element="${name}"]`) || null;
    }
    /**
     * Helper to get template elements within the sales event widget
     * @param name Template element name
     * @returns Template element or null if not found
     */
    static getTemplate(name) {
        return SalesEvent.widget?.querySelector(`[data-se-template="${name}"]`) || null;
    }
    /**
     * Helper to get translation strings with replacements
     * @param key Translation key in window.languages
     * @param replacements Object containing values for replacement keys
     * @returns Translation value with replacements if existing
     */
    static getTranslation(key, replacements) {
        return window.languages.salesEvent[key] ? window.languages.salesEvent[key].replace(/\{(\w+)\}/g, (_, p1) => replacements && replacements[p1] !== undefined ? replacements[p1].toString() : '') : `Translation missing: ${key}`;
    }
    /**
     * Generate image HTML based on given image data
     * @param image Image data
     * @returns Image HTML wrapped in AspectRatio div
     */
    static generateImageHtml(image) {
        return `
      <div class="AspectRatio ${image.wrapperClass || ''}" style="--ratio: ${image.aspectRatio || image.width / image.height};">
        <img class="img-loading"
          loading="lazy"
          data-ab-image
          alt="${image.alt || ''}"
          sizes="${image.sizes || '(max-width: 1279px) calc(100vw - 2rem), (max-width: 1279px) calc(100vw - 5rem), (max-width: 1439px) calc(100vw - 7.5rem), 1440px'}"
          srcset="${(image.widths ? image.widths : [100, 150, 200, 400, 500, 600, 650, 700, 800, 900, 1200, 1500, 2000]).map(width => `${image.url}${image.url.includes('?') ? '&' : '?'}width=${width} ${width}w`).join(', ')}"
          width="${image.width}"
          height="${image.height}"
          onload="this.classList.remove('img-loading');"
          onerror="this.classList.remove('img-loading');"
        >
      </div>`;
    }
}
SalesEvent.config = null;
SalesEvent.cart = null;
SalesEvent.uiData = {
    blocked: false,
    headline: '',
    thresholds: [],
    cartValue: 0
};
SalesEvent.widget = null;
window.SalesEvent = SalesEvent;
